import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { HeroSection } from './components/HeroSection';
import { UploadForm } from './components/UploadForm';
import { ResultSection } from './components/ResultSection';
import { HowItWorks } from './components/HowItWorks';
import { Footer } from '../../shared/components/Footer';
import { SEO } from '../../shared/components/SEO';
import { toast } from 'sonner';
import { generateProfilePicture } from './utils/profilePictureGenerator';
import { useAuth } from '../../contexts/AuthContext';
import { AuthModal, AuthButton, CreditConfirmModal } from '../../components/auth';

const ProfilePictureGeneratorTool = () => {
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState('login');
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  
  const { isAuthenticated, credits, refreshUserProfile } = useAuth();

  const seoData = {
    title: 'Professional LinkedIn Profile Picture Generator | Free AI Headshot Tool by Growleads.io',
    description: 'Transform your photo into a professional LinkedIn headshot instantly. Free AI-powered tool that creates studio-quality profile pictures with customizable expressions, backgrounds, and lighting. Perfect for resumes, LinkedIn, and professional platforms.',
    keywords: 'LinkedIn profile picture, professional headshot generator, AI headshot, profile photo maker, LinkedIn photo, professional photo generator, headshot AI, business profile picture, free headshot tool, LinkedIn image optimizer, professional portrait, AI photo editor, corporate headshot, resume photo, Growleads',
    canonicalUrl: 'https://tools.growleads.io/linkedin-profile-picture-generator',
    ogImage: `${window.location.origin}/profile-tool-trans.png`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Professional Profile Picture Generator",
      "description": "Transform your photo into a professional LinkedIn headshot instantly with AI-powered customization",
      "url": "https://tools.growleads.io/linkedin-profile-picture-generator",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "creator": {
        "@type": "Organization",
        "name": "Growleads.io",
        "url": "https://growleads.io",
        "logo": "https://growleads.io/logo.png"
      },
      "featureList": [
        "AI-powered headshot generation",
        "Identity preservation",
        "Customizable expressions and backgrounds",
        "Professional lighting options",
        "Instant download",
        "Free to use"
      ],
      "screenshot": `${window.location.origin}/profile-tool-trans.png`
    }
  };

  const handleGenerate = async (imageFile, personalization) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setPendingData({ imageFile, personalization });
      setAuthModalView('login');
      setShowAuthModal(true);
      return;
    }
    
    // Check if user has credits
    if (credits < 1) {
      toast.error('You have no credits left. Please upgrade to continue.');
      return;
    }
    
    // Show credit confirmation modal
    setPendingData({ imageFile, personalization });
    setShowCreditModal(true);
  };
  
  const executeGeneration = async (data) => {
    setIsLoading(true);
    setResult(null);
    
    try {
      const generatedResult = await generateProfilePicture(data.imageFile, data.personalization);
      setResult(generatedResult);
      
      // Refresh user profile to update credits display
      await refreshUserProfile();
      
      // Scroll to results
      setTimeout(() => {
        document.querySelector('#results')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error generating profile picture:', error);
      
      if (error.message === 'AUTH_REQUIRED') {
        setPendingData(data);
        setAuthModalView('login');
        setShowAuthModal(true);
        return;
      }
      
      if (error.message === 'INSUFFICIENT_CREDITS') {
        toast.error('You have no credits left. Please upgrade to continue.');
        return;
      }
      
      toast.error(error.message || 'Failed to generate profile picture. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreditConfirm = () => {
    setShowCreditModal(false);
    if (pendingData) {
      executeGeneration(pendingData);
      setPendingData(null);
    }
  };

  const handleReset = () => {
    setResult(null);
  };

  const handleRegenerate = () => {
    setResult(null);
    // Scroll to upload section
    setTimeout(() => {
      document.querySelector('#generator')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO {...seoData} />
      
      {/* Tool Header */}
      <header className="w-full border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link 
                to="/" 
                className="flex items-center gap-1 sm:gap-2 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium hidden xs:inline">All Tools</span>
              </Link>
              <div className="h-6 w-px bg-border flex-shrink-0 hidden sm:block" />
              <a href="https://growleads.io" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-90 transition-opacity min-w-0">
                <img
                  src="/profile-tool-trans.png"
                  alt="Profile Picture Generator"
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl object-contain flex-shrink-0"
                />
                <span className="font-display font-semibold text-foreground text-sm sm:text-lg truncate hidden sm:block">Profile Picture Generator</span>
              </a>
            </div>
            <nav className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <div className="hidden md:flex items-center gap-6">
                <a href="#generator" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Generator</a>
                <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">How It Works</a>
              </div>
              <AuthButton />
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <HeroSection />
        <HowItWorks />
        <UploadForm onGenerate={handleGenerate} isLoading={isLoading} onReset={handleReset} hasResult={!!result} />
        {result && (
          <div id="results">
            <ResultSection result={result} onRegenerate={handleRegenerate} />
          </div>
        )}
      </main>
      <Footer 
        logoSrc="/profile-tool-trans.png"
        brandLabel="Profile Picture Generator"
        ctaTitle="Want a Complete LinkedIn Profile Optimization?"
        ctaDescription="Use our LinkedIn Profile Optimizer tool to analyze and optimize your entire LinkedIn profile for better visibility and engagement."
      />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => {
          setShowAuthModal(false);
          setPendingData(null);
        }}
        defaultView={authModalView}
      />
      
      {/* Credit Confirmation Modal */}
      <CreditConfirmModal
        isOpen={showCreditModal}
        onClose={() => {
          setShowCreditModal(false);
          setPendingData(null);
        }}
        onConfirm={handleCreditConfirm}
        credits={1}
        toolName="Profile Picture Generator"
        currentCredits={credits}
      />
    </div>
  );
};

export default ProfilePictureGeneratorTool;
